import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../services/api.service';
import { ProfileService } from '../../services/profile.service';
import { HttpClientModule } from '@angular/common/http';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { usaPhoneValidator } from '../../shared/validators/phone.validator';
import { strictEmailValidator } from '../../shared/validators/email.validator';
import { standardNameValidator } from '../../shared/validators/name.validator';
import { Subscription } from 'rxjs';
import { sanitizeErrorMessage } from '../../shared/utils/error.util';

/**
 * Manage and edit a user's profile details including name, email, phone number and profile image;
 * handles form validation, image selection/validation/removal, change tracking, and submitting updates to a ProfileService.
 * Key behaviors: loads incoming userProfile into a reactive form, tracks whether editable fields or the profile image have changed,
 * validates file type and size for uploads (accepts JPG/PNG/GIF up to 20MB), supports canceling edits (restores original data),
 * and submits changes via profileService.updateUserProfile with proper FormData handling.
 * Important lifecycle hooks:
 *   - ngOnChanges: reacts to changes to the @input userProfile and updates local form/image state.
 *   - ngOnDestroy: unsubscribes from internal subscriptions to avoid memory leaks.
 * Key methods:
 *   - updateLocalProfileData(profile): patches the reactive form and image URL, stores originals for cancellation/comparison.
 *   - onFileSelected(event): validates and previews selected image files, tracks upload errors and size/type constraints.
 *   - removePhoto(): clears the profile image selection and marks the photo as removed.
 *   - checkIfFormModified(): compares current editable fields and image state against originals to enable/disable save.
 *   - onEdit(), onCancel(), onSubmit(): manage edit mode, restore original values on cancel, and submit changes with proper handling of image changes and API responses.
 * @component
 * @selector app-profile-details
 * @example
 *   <app-profile-details [userProfile]="sampleUser" (cancelClicked)="handleCancel()"></app-profile-details>
 * @input any userProfile - The incoming profile object used to populate the form and image (expected fields: name, emp_id, email, contact_number, picture_url).
 * @output EventEmitter<void> cancelClicked - Emitted when editing is cancelled or after a successful save to notify parent components.
 */
@Component({
  selector: 'app-profile-details',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, HttpClientModule],
  templateUrl: './profile-details.component.html',
  styleUrls: ['./profile-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ApiService],
})
export class ProfileDetailsComponent implements OnChanges, OnDestroy {
  @Input() userProfile: any;
  @Output() cancelClicked = new EventEmitter<void>();
  profileForm: FormGroup;
  isEditing = false;
  profileImageUrl: string | null = null;
  selectedFile: File | null = null;
  uploadError: string | null = null;
  isImageUploading = false;
  private photoWasRemoved = false;
  isFormModified = false; // Starts as false - button disabled until changes are made
  isSubmitting = false;
  private subscriptions: Subscription = new Subscription();

  // Store original data for cancellation
  private originalFormData: any = {};
  private originalProfileImageUrl: string | null = null;

  /**
   * Initializes the component: builds the profile form with validation, and subscribes to form value changes and profile updates.
   * @example
   * new ProfileDetailsComponent(fb, apiService, profileService, messageService)
   * ProfileDetailsComponent instance
   * @param {FormBuilder} fb - Angular FormBuilder used to construct the reactive profile form.
   * @param {ApiService} apiService - Service for making API calls related to profile operations.
   * @param {ProfileService} profileService - Service that provides the userProfile$ observable to receive profile updates.
   * @param {NotificationService} messageService - Service used to display user messages and notifications.
   * @returns {void} Constructor does not return a value.
   **/
  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private profileService: ProfileService,
    private messageService: NotificationService
  ) {
    this.profileForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.maxLength(100), standardNameValidator()]],
      employeeId: [
        { value: 'EMP#143', disabled: true },
        [Validators.required, Validators.maxLength(50)],
      ],
      email: ['', [Validators.required, Validators.maxLength(254), strictEmailValidator()]],
      phoneNumber: ['', [Validators.required, Validators.maxLength(15), usaPhoneValidator()]],
    });

    // Track form changes
    this.subscriptions.add(
      this.profileForm.valueChanges.subscribe(() => {
        // if (this.isEditing) {
        this.checkIfFormModified();
        // }
      })
    );

    // Subscribe to profile changes to update local data when profile is refreshed
    this.subscriptions.add(
      this.profileService.userProfile$.subscribe(updatedProfile => {
        if (updatedProfile) {
          // Only update if we're not currently editing to avoid overriding user changes
          this.updateLocalProfileData(updatedProfile);
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userProfile'] && changes['userProfile'].currentValue) {
      this.updateLocalProfileData(this.userProfile);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Update local component state and form controls with the provided profile data.
   * @example
   * updateLocalProfileData({
   *   name: 'Jane Doe',
   *   emp_id: 'E123',
   *   email: 'jane.doe@example.com',
   *   contact_number: '+1234567890',
   *   picture_url: 'https://example.com/avatar.jpg'
   * })
   * undefined
   * @param {{any}} {{profile}} - Profile object containing name, emp_id, email, contact_number, and optional picture_url.
   * @returns {{void}} No return value; updates component state, original form snapshot, form controls, and image-related fields.
   **/
  private updateLocalProfileData(profile: any): void {
    const formData = {
      fullName: profile.name,
      employeeId: profile.emp_id,
      email: profile.email,
      phoneNumber: profile.contact_number,
    };

    // Store original data BEFORE patching (only editable fields for comparison)
    this.originalFormData = {
      fullName: profile.name,
      email: profile.email,
      phoneNumber: profile.contact_number,
    };
    this.originalProfileImageUrl = profile.picture_url || null;

    this.profileForm.patchValue(formData, { emitEvent: false });

    if (profile.picture_url) {
      this.profileImageUrl = profile.picture_url;
    } else {
      this.profileImageUrl = null;
    }

    // Reset form modification state after loading data
    this.isFormModified = false;
    this.selectedFile = null;
    this.photoWasRemoved = false;
  }

  /**
   * Handle file selection from a file input: validate image type/size, read as data URL, and update component state.
   * @example
   * nFileSelected(event)
   * void
   * @param {{Event}} {{event}} - The input change event from an <input type="file"> element containing the selected file.
   * @returns {{void}} No return value; updates component properties (selectedFile, profileImageUrl, uploadError, isImageUploading, photoWasRemoved) and invokes checkIfFormModified.
   **/
  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const [file] = target.files ? Array.from(target.files) : [];

    if (file) {
      this.uploadError = null;
      this.photoWasRemoved = false;
      this.isImageUploading = true;

      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        this.uploadError = 'Unsupported file format. Please upload a JPG, PNG, or GIF image.';
        this.selectedFile = null;
        this.isImageUploading = false;
        target.value = '';
        return;
      }

      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        this.uploadError = 'File exceeds 20MB size limit.';
        this.selectedFile = null;
        this.isImageUploading = false;
        target.value = '';
        return;
      }

      this.selectedFile = file;

      const reader = new FileReader();
      reader.onload = () => {
        this.profileImageUrl = reader.result as string;
        this.isImageUploading = false;

        this.checkIfFormModified();
      };
      reader.onerror = () => {
        this.uploadError = 'Error reading file. Please try again.';
        this.isImageUploading = false;
        this.selectedFile = null;
        target.value = '';
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Remove the currently selected profile photo, clear the file input element, and mark the photo as removed before checking if the form was modified.
   * @example
   * emovePhoto()
   * // undefined
   * @returns {void} Does not return a value.
   */
  removePhoto(): void {
    this.profileImageUrl = '';
    this.selectedFile = null;
    this.photoWasRemoved = true;
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    this.checkIfFormModified();
  }

  get initials(): string {
    const name = this.userProfile?.name;
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  get fullName() {
    return this.profileForm.get('fullName');
  }

  get email() {
    return this.profileForm.get('email');
  }

  get phoneNumber() {
    return this.profileForm.get('phoneNumber');
  }

  /**
   * Submit the profile form (name, email, phone, and optional picture) to update the user's profile on the server and manage UI state, notifications, and error handling.
   * @example
   * nSubmit()
   * // void - triggers an HTTP request, updates component state, and shows success/error messages
   * @param {void} none - No arguments.
   * @returns {void} Performs side effects (HTTP update, UI state changes, event emission) and does not return a value.
   **/
  onSubmit() {
    if (this.profileForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;

      const formData = new FormData();
      formData.append('name', this.profileForm.value.fullName);
      formData.append('email', this.profileForm.value.email);
      formData.append('contact_number', this.profileForm.value.phoneNumber);

      // Only send image data if there are actual image changes
      if (this.selectedFile) {
        formData.append('picture_url', this.selectedFile, this.selectedFile.name);
      } else if (this.photoWasRemoved) {
        formData.append('picture_url', '');
      }
      // If no image changes, don't send any image field to preserve existing image

      this.profileService.updateUserProfile(formData).subscribe({
        next: response => {
          // Update original data after successful save to match current state (only editable fields)
          this.originalFormData = {
            fullName: this.profileForm.value.fullName,
            email: this.profileForm.value.email,
            phoneNumber: this.profileForm.value.phoneNumber,
          };
          this.originalProfileImageUrl = this.profileImageUrl;

          // Reset all modification tracking
          this.isEditing = false;
          this.isFormModified = false;
          this.selectedFile = null;
          this.uploadError = null;
          this.photoWasRemoved = false;
          this.isSubmitting = false;
          this.cancelClicked.emit();
          this.messageService.add({
            severity: 'success',
            summary: 'Changes Saved!',
            detail: 'Your changes have been saved successfully',
          });
        },
        error: error => {
          console.error('Error updating profile', error);
          this.isSubmitting = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: sanitizeErrorMessage(
              error.error?.message || 'An error occurred while updating your profile.'
            ),
          });
        },
      });
    }
  }

  /**
   * Enable profile editing: save current editable fields (fullName, email, phoneNumber) and profile image URL as backups, set editing mode, and reset modification tracking.
   * @example
   * nEdit()
   * undefined
   * @returns {void} No return value; updates component state to enter edit mode and stores original values.
   */
  onEdit() {
    // Store current form values and profile image as backup before enabling editing (only editable fields)
    const currentFormData = this.profileForm.value;
    this.originalFormData = {
      fullName: currentFormData.fullName,
      email: currentFormData.email,
      phoneNumber: currentFormData.phoneNumber,
    };
    this.originalProfileImageUrl = this.profileImageUrl;
    this.isEditing = true;
    this.isFormModified = false; // Reset modification tracking
  }

  /**
   * Cancel editing and restore the profile form to its original state, clear the file input and image selection, and emit the cancel event.
   * @example
   * nCancel()
   * undefined
   * @returns {{void}} No return value; resets form state, restores original data and image, clears upload state, and emits the cancelClicked event.
   **/
  onCancel() {
    this.isEditing = false;
    this.isFormModified = false;
    // Restore original form data and profile image
    this.profileForm.patchValue(this.originalFormData);
    this.profileImageUrl = this.originalProfileImageUrl;
    this.selectedFile = null;
    this.uploadError = null;
    this.photoWasRemoved = false;
    // Clear file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.cancelClicked.emit();
  }

  onPhoneKeyup() {
    // Trigger validation on keyup
    const phoneControl = this.profileForm.get('phoneNumber');
    if (phoneControl) {
      phoneControl.markAsTouched();
      phoneControl.updateValueAndValidity();
    }
  }

  /**
   * Check whether editable profile form fields or the profile image have been modified and update the isFormModified flag.
   * @example
   * checkIfFormModified()
   * undefined
   * @param {{void}} none - No parameters.
   * @returns {{void}} Updates the component's isFormModified property based on form and image comparisons; does not return a value.
   **/
  private checkIfFormModified() {
    // Get current form values (excludes disabled fields like employeeId)
    const currentFormData = this.profileForm.value;
    const currentImageUrl = this.profileImageUrl;

    // Compare only editable fields (exclude employeeId which is disabled)
    const editableFields = {
      fullName: currentFormData.fullName,
      email: currentFormData.email,
      phoneNumber: currentFormData.phoneNumber,
    };

    // Check if form data has changed
    const formDataChanged =
      JSON.stringify(editableFields) !== JSON.stringify(this.originalFormData);

    // Check if image has changed
    const imageChanged =
      currentImageUrl !== this.originalProfileImageUrl ||
      this.selectedFile !== null ||
      this.photoWasRemoved;

    this.isFormModified = formDataChanged || imageChanged;
  }
}
